import { DataObjType } from '@customTypes/dataLayer';
import { hasDataLayer } from '@utils/dataLayer';

const DEBOUNCE_DELAY = 50;
const TOLERANCE = 1;

export const Swiper = (swiperElement: HTMLDivElement) => {
  const container = swiperElement.querySelector<HTMLDivElement>('[data-swiper-container]');
  const list = swiperElement.querySelector<HTMLDivElement>('[data-swiper-list]');

  if (!container || !list) {
    throw new Error('Swiper container nebo swiper list nebyl nalezen.');
  }

  const navigation = swiperElement.querySelector<HTMLDivElement>('[data-swiper-navigation]');
  const prevArrow = swiperElement.querySelector<HTMLButtonElement>('[data-swiper-arrow-prev]');
  const nextArrow = swiperElement.querySelector<HTMLButtonElement>('[data-swiper-arrow-next]');
  const items = [...swiperElement.querySelectorAll<HTMLElement>('[data-swiper-item]')];
  const navigationItems = navigation
    ? [...(navigation.querySelectorAll<HTMLElement>('[data-swiper-navigation-item]') || [])]
    : [];
  const shouldShowEndFade = swiperElement.hasAttribute('data-swiper-end-fade') && swiperElement.getAttribute("data-swiper-end-fade") === "true";

  let currentIndex = 0;
  let dataScrollTimeout: ReturnType<typeof setTimeout> | null = null;
  let isDragging = false;
  let startX = 0;
  let scrollLeft = 0;
  
  // ResizeObserver pro sledování změn velikosti okna
  let resizeObserver: ResizeObserver | null = null;

  const isItemVisible = (item: HTMLElement, container: HTMLElement): boolean => {
    const containerRect = container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    
    return (
      itemRect.left >= containerRect.left &&
      itemRect.right <= containerRect.right
    );
  };

  const initializeActiveItem = () => {
    const activeIndex = parseInt(swiperElement.getAttribute('data-active-index') || '0', 10);
    
    if (activeIndex > 0 && activeIndex < items.length) {
      const activeItem = items[activeIndex];
      setCurrentIndex(activeIndex);
      
      if (!isItemVisible(activeItem, container)) {
        list.scrollLeft = activeItem.offsetLeft - container.offsetLeft;
      }
    }
  };

  const handleSwiperItemGtm = () => {
    const evData = items[currentIndex]?.getAttribute('data-track-swiper-gtm');

    if (!evData || !hasDataLayer()) return;

    const evDataParsed = JSON.parse(evData) as DataObjType;

    void window.dataLayer.push(evDataParsed);
  };

  const setCurrentIndex = (index: number) => {
    const hasChangedIndex = currentIndex !== index;
    currentIndex = index;

    if (hasChangedIndex) handleSwiperItemGtm();
  };

  const getCurrentIndex = () => {
    return currentIndex;
  };

  const updateNavigation = () => {
    navigationItems.forEach((item, index) => {
      item.classList.toggle('is-active', index === currentIndex);
    });
  };

  const scrollToItem = (index: number) => {
    if (index >= 0 && index < items.length) {
      const item = items[index];
      const targetLeft = item.offsetLeft - list.offsetLeft;

      list.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      });
    }
  };

  const isAtStartOfScroll = () => {
    return list.scrollLeft <= 0;
  };

  const isAtEndOfScroll = () => {
    return list.scrollLeft + list.clientWidth >= list.scrollWidth - TOLERANCE;
  };

  const updateArrowsVisibility = () => {
    if (prevArrow) {
      prevArrow.classList.toggle('hidden', isAtStartOfScroll());
    }
    if (nextArrow) {
      nextArrow.classList.toggle('hidden', isAtEndOfScroll());
    }
  };

  const handleArrowClick = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
        const newIndex = currentIndex - 1;
        setCurrentIndex(newIndex);
        scrollToItem(newIndex);
      
    } else {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      scrollToItem(newIndex);
    }
  };

  const updateEndFadeClass = () => {
    if (shouldShowEndFade) {
      container.classList.toggle('end-fade', !isAtEndOfScroll());
    } else {
      container.classList.remove('end-fade');
    }
  };

  const updateSelectedItemOnScroll = () => {
    const listLeft = list.scrollLeft;

    let closestIndex = 0;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - listLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    
    setCurrentIndex(closestIndex);

    if (navigation) updateNavigation();
    updateArrowsVisibility();
  };

  const handleScroll = () => {
    updateEndFadeClass();
    updateArrowsVisibility();

    if (dataScrollTimeout !== null) {
      clearTimeout(dataScrollTimeout);
    }
    dataScrollTimeout = setTimeout(() => {
      updateSelectedItemOnScroll();
    }, DEBOUNCE_DELAY);
  };

  const handleTouchend = () => {
    if (isAtEndOfScroll()) {
      setCurrentIndex(items.length - 1);
      if (navigation) updateNavigation();
      updateArrowsVisibility();
    }
  };

  const handleNavigationItemClick = (index: number) => {
    if (index !== -1) {
      setCurrentIndex(index);
      scrollToItem(index);
    }
  };

  const handleMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    isDragging = true;
    startX = e.pageX - list.offsetLeft;
    scrollLeft = list.scrollLeft;

    list.classList.add('is-mouse-drag');
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    const x = e.pageX - list.offsetLeft;
    const walk = (x - startX) * 2;

    requestAnimationFrame(() => {
      list.scrollLeft = scrollLeft - walk;
    });
  };

  const handleMouseUp = () => {
    isDragging = false;
    list.classList.remove('is-mouse-drag');
  };

  // Nová metoda pro aktualizaci UI při změně velikosti
  const handleResize = () => {
    updateArrowsVisibility();
    updateEndFadeClass();
  };

  // Vytvoření pojmenovaných handler funkcí pro šipky
  const prevArrowHandler = () => handleArrowClick('prev');
  const nextArrowHandler = () => handleArrowClick('next');

  list.addEventListener('scroll', handleScroll);
  list.addEventListener('touchend', handleTouchend);
  container.addEventListener('mousedown', handleMouseDown);
  container.addEventListener('mousemove', handleMouseMove);
  container.addEventListener('mouseup', handleMouseUp);

  if (prevArrow) {
    prevArrow.addEventListener('click', prevArrowHandler);
  }
  if (nextArrow) {
    nextArrow.addEventListener('click', nextArrowHandler);
  }

  // Inicializace ResizeObserveru
  if ('ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);
    
    // Také sledujeme změny velikosti okna, které mohou ovlivnit kontejner
    resizeObserver.observe(document.documentElement);
  } else {
    // Fallback pro starší prohlížeče
    (window as Window).addEventListener('resize', handleResize);
  }

  updateEndFadeClass();
  updateArrowsVisibility();

  if (navigation) {
    navigationItems.forEach((item, index) => {
      item.addEventListener('click', handleNavigationItemClick.bind(null, index));
    });
    updateNavigation();
  }

  initializeActiveItem();

  // Rozšířený return objekt obsahující metodu pro cleanup
  return {
    scrollToItem,
    getCurrentIndex,
    destroy: () => {
      // Vyčištění event listenerů
      list.removeEventListener('scroll', handleScroll);
      list.removeEventListener('touchend', handleTouchend);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);

      if (prevArrow) {
        prevArrow.removeEventListener('click', prevArrowHandler);
      }
      if (nextArrow) {
        nextArrow.removeEventListener('click', nextArrowHandler);
      }

      // Vyčištění ResizeObserveru
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      } else {
        window.removeEventListener('resize', handleResize);
      }
    }
  };
};

export default Swiper;
